/**
 * Card hover interaction for the Native Interactive Site Map listing panel.
 *
 * Event delegation over the panel root — cards are re-rendered via innerHTML swaps constantly
 * (chip switch, tab switch, lot change in lotMapSelection), so listeners bind once on the stable
 * panel container instead of being re-attached per card. Pure DOM — no Redux, no CSS, no
 * business logic. Mirrors the delegation style of
 * libraries/mapLibrary/svgLotInteraction.js (bind/unbind, locally-tracked "currently active"
 * state to dedupe repeated events).
 *
 * @module nativeInteractiveSiteMap/handlers/cardHoverInteractionHandlers
 */

import ListingType from 'enums/listingType';

const CARD_IDENTITY_SELECTOR = '[data-plan-id], [data-housing-unit-id]';

/**
 * @param {HTMLElement} cardElement
 * @returns {{ type: ListingType, id: string }|null}
 */
function readCardIdentity(cardElement) {
  if (cardElement.dataset.planId) {
    return { type: ListingType.HousePlan, id: cardElement.dataset.planId };
  }
  if (cardElement.dataset.housingUnitId) {
    return { type: ListingType.HousingUnit, id: cardElement.dataset.housingUnitId };
  }
  return null;
}

/**
 * @param {object} handlers
 * @param {Function} handlers.onCardHovered - Called with { type, id } when a new card is entered
 * @param {Function} handlers.onCardLeft - Called (no args) when the pointer leaves the
 *   currently-hovered card without entering another one
 * @returns {{ bind: Function, unbind: Function }}
 */
function createCardHoverInteractionHandlers({ onCardHovered, onCardLeft }) {
  const state = {
    currentCardElement: null,
  };

  function handleMouseOver(event) {
    const cardElement = event.target.closest(CARD_IDENTITY_SELECTOR);
    if (!cardElement || cardElement === state.currentCardElement) {
      return;
    }
    const identity = readCardIdentity(cardElement);
    if (!identity) {
      return;
    }
    state.currentCardElement = cardElement;
    onCardHovered(identity);
  }

  function handleMouseOut(event) {
    if (!state.currentCardElement) {
      return;
    }
    // relatedTarget is null when the pointer leaves the document/window entirely, and is some
    // element inside the same card when mouseout fired only because the pointer crossed from a
    // parent to a child (mouseout bubbles) — only treat this as "left the card" when neither.
    if (event.relatedTarget && state.currentCardElement.contains(event.relatedTarget)) {
      return;
    }
    state.currentCardElement = null;
    onCardLeft();
  }

  function bind(rootElement) {
    rootElement.addEventListener('mouseover', handleMouseOver);
    rootElement.addEventListener('mouseout', handleMouseOut);
  }

  function unbind(rootElement) {
    rootElement.removeEventListener('mouseover', handleMouseOver);
    rootElement.removeEventListener('mouseout', handleMouseOut);
  }

  return { bind, unbind };
}

export default createCardHoverInteractionHandlers;
